import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { TreningDto, ZalozenieTreninguDto } from "@shared/treningTypy.js";
import {
  KOD_NIE_MA_ZRODLA,
  czyEkranNieaktualny,
  czyTuraWeszla,
  czyWynikWysylkiNiepewny,
  normalizujOdpowiedz,
  odczytajNiepewnaWysylke,
  opisBleduTreningu,
  usunNiepewnaWysylke,
  zapiszNiepewnaWysylke,
  type BladTreningu,
} from "../lib/trening.js";
import { treningApi } from "../lib/treningApi.js";

/**
 * Hooki ekranow treningu. Stan zmienia sie WYLACZNIE po akcjach uzytkownika (wejscie na ekran, wyslanie odpowiedzi,
 * zakonczenie, "Odswiez rozmowe"): zadnego odpytywania w tle, bo kazda tura to platne wywolanie modelu po stronie serwera,
 * a rozmowca odpowiada w tej samej odpowiedzi HTTP, na ktora uzytkownik i tak czeka.
 */

export type AkcjaTreningu = "wyslij" | "zakoncz" | "odswiez";

/** Blad ostatniej akcji z informacja, ktora to byla akcja (inny komunikat pod polem, inny przy przycisku zakonczenia). */
export interface BladAkcji extends BladTreningu {
  akcja: AkcjaTreningu;
}

export interface StanTreningu {
  trening: TreningDto | null;
  /** Pierwsze pobranie (przed jakimikolwiek danymi). Ponowne pobranie przy istniejacych danych jest ciche (S-7). */
  ladowanie: boolean;
  /** Blad pobrania, gdy nie ma jeszcze zadnych danych (404, siec). */
  bladLadowania: BladTreningu | null;
  /** Trwa wysylanie odpowiedzi (czekamy na rozmowce). */
  wysylanie: boolean;
  /** Trwa zakonczenie (ocena). */
  konczenie: boolean;
  bladAkcji: BladAkcji | null;
  /** True po udanym wyslaniu. Przy bledzie tekst zostaje u wywolujacego (pole nie jest czyszczone). */
  wyslij: (tekst: string) => Promise<boolean>;
  zakoncz: () => Promise<boolean>;
  /** Pobiera trening jeszcze raz (np. po nieudanym pierwszym pobraniu albo gdy rozmowca zdazyl odpowiedziec). */
  odswiez: () => void;
}

export function useTrening(id: string): StanTreningu {
  const [trening, setTrening] = useState<TreningDto | null>(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [bladLadowania, setBladLadowania] = useState<BladTreningu | null>(null);
  const [wysylanie, setWysylanie] = useState(false);
  const [konczenie, setKonczenie] = useState(false);
  const [bladAkcji, setBladAkcji] = useState<BladAkcji | null>(null);

  const idRef = useRef(id);
  idRef.current = id;
  /** Blokada podwojnego klikniecia: stan Reacta zmienia sie dopiero w nastepnym renderze, ref od razu. */
  const zajeteRef = useRef(false);
  /** Odpowiedz starsza niz ostatnio zlozone zapytanie jest pomijana (zmiana treningu w trakcie pobierania). */
  const numerPobrania = useRef(0);
  const treningRef = useRef<TreningDto | null>(null);

  const przyjmij = useCallback((dane: TreningDto) => {
    treningRef.current = dane;
    setTrening(dane);
  }, []);

  const pobierz = useCallback(async (): Promise<void> => {
    const numer = ++numerPobrania.current;
    const cicho = treningRef.current !== null;
    if (!cicho) {
      setLadowanie(true);
      // Ponowienie po nieudanym pierwszym wczytaniu: ekran bledu znika na czas zapytania (pokazuje sie ladowanie), zeby
      // "Spróbuj ponownie" dawalo sygnal pracy i nie zachecalo do wielokrotnego klikania.
      setBladLadowania(null);
    }
    try {
      const dane = await treningApi.pobierz(id);
      if (numer !== numerPobrania.current) return;
      przyjmij(dane);
      setBladLadowania(null);
    } catch (blad) {
      if (numer !== numerPobrania.current) return;
      const opis = opisBleduTreningu(blad);
      // Ciche odswiezenie, ktore sie nie udalo, zostawia rozmowe na ekranie i mowi o tym przy akcji, ktora je wywolala.
      if (cicho) setBladAkcji({ ...opis, akcja: "odswiez" });
      else setBladLadowania(opis);
    } finally {
      if (numer === numerPobrania.current) setLadowanie(false);
    }
  }, [id, przyjmij]);

  useEffect(() => {
    treningRef.current = null;
    setTrening(null);
    setBladLadowania(null);
    setBladAkcji(null);
    void pobierz();
  }, [pobierz]);

  /** Stan treningu z serwera BEZ ruszania ekranu; null, gdy sie nie udalo (wtedy nie wiemy nic wiecej). */
  const sprawdzStan = useCallback(async (): Promise<TreningDto | null> => {
    try {
      return await treningApi.pobierz(id);
    } catch {
      return null;
    }
  }, [id]);

  /**
   * Jedna akcja uzytkownika (wyslanie albo zakonczenie). `czyUdaloSie` dostaje stan z serwera po bledzie o NIEPEWNYM
   * wyniku (zerwane polaczenie, 504 z proxy, trwajace zadanie): jesli akcja jednak weszla, ekran pokazuje ten stan jak po
   * sukcesie, zamiast kazac ponawiac krok, ktory juz sie wykonal.
   */
  const wykonaj = useCallback(
    async (
      akcja: "wyslij" | "zakoncz",
      uruchom: () => Promise<TreningDto>,
      czyUdaloSie?: (stan: TreningDto) => boolean,
      poPewnymWyniku?: () => void,
    ): Promise<boolean> => {
      if (zajeteRef.current) return false;
      zajeteRef.current = true;
      (akcja === "wyslij" ? setWysylanie : setKonczenie)(true);
      setBladAkcji(null);
      const dlaId = id;
      try {
        const dane = await uruchom();
        poPewnymWyniku?.();
        if (idRef.current === dlaId) przyjmij(dane);
        return true;
      } catch (blad) {
        const opis = opisBleduTreningu(blad);
        if (czyUdaloSie && czyWynikWysylkiNiepewny(opis)) {
          const stan = await sprawdzStan();
          if (stan !== null && czyUdaloSie(stan)) {
            poPewnymWyniku?.();
            if (idRef.current === dlaId) przyjmij(stan);
            return true;
          }
          // Nie weszla (jeszcze): ekran zostaje przy bledzie, a znacznik niepewnej wysylki zostaje do ponowienia.
        } else {
          poPewnymWyniku?.();
        }
        setBladAkcji({ ...opis, akcja });
        // Ekran jest nieaktualny (np. trening zakonczony w innej karcie): pobieramy go jeszcze raz, ale to nadal skutek
        // klikniecia uzytkownika, nie odpytywanie w tle.
        if (czyEkranNieaktualny(opis.kod)) void pobierz();
        return false;
      } finally {
        zajeteRef.current = false;
        (akcja === "wyslij" ? setWysylanie : setKonczenie)(false);
      }
    },
    [id, pobierz, przyjmij, sprawdzStan],
  );

  const wyslij = useCallback(
    async (tekst: string): Promise<boolean> => {
      if (zajeteRef.current) return false;
      const normalnie = normalizujOdpowiedz(tekst);
      const znacznik = odczytajNiepewnaWysylke(id);
      const ponowienie = znacznik !== null && znacznik.tekst === normalnie;
      const przed = ponowienie ? znacznik.przed : (treningRef.current?.liczbaTurUcznia ?? 0);

      if (ponowienie) {
        // Poprzednia proba TEGO SAMEGO tekstu mogla przejsc (takze przed odswiezeniem strony): najpierw sprawdzamy serwer,
        // zeby nie dopisac drugiej, identycznej odpowiedzi.
        zajeteRef.current = true;
        setWysylanie(true);
        setBladAkcji(null);
        try {
          const stan = await sprawdzStan();
          if (stan !== null && czyTuraWeszla(stan, normalnie, przed)) {
            usunNiepewnaWysylke(id);
            if (idRef.current === id) przyjmij(stan);
            return true;
          }
        } finally {
          zajeteRef.current = false;
          setWysylanie(false);
        }
      }

      // Znacznik stoi od wyslania do pewnego wyniku. Przy bledzie niepewnym (patrz wykonaj) zostaje.
      zapiszNiepewnaWysylke(id, { tekst: normalnie, przed });
      let niepewny = false;
      const udalo = await wykonaj(
        "wyslij",
        async () => {
          try {
            return await treningApi.wyslij(id, tekst);
          } catch (blad) {
            niepewny = czyWynikWysylkiNiepewny(opisBleduTreningu(blad));
            throw blad;
          }
        },
        (stan) => czyTuraWeszla(stan, normalnie, przed),
        () => {
          // Wynik jest pewny (sukces, albo blad serwera z kodem): znacznik niepotrzebny. Niepewny zostaje.
          if (!niepewny) usunNiepewnaWysylke(id);
        },
      );
      if (udalo) usunNiepewnaWysylke(id);
      return udalo;
    },
    [id, przyjmij, sprawdzStan, wykonaj],
  );

  const zakoncz = useCallback(
    () =>
      wykonaj(
        "zakoncz",
        () => treningApi.zakoncz(id),
        // Zakonczenie, ktorego odpowiedz zginela po drodze (504), moglo sie jednak wykonac: trening jest wtedy zakonczony.
        (stan) => stan.status === "FINISHED",
      ),
    [id, wykonaj],
  );

  const odswiez = useCallback(() => {
    setBladAkcji(null);
    void pobierz();
  }, [pobierz]);

  return { trening, ladowanie, bladLadowania, wysylanie, konczenie, bladAkcji, wyslij, zakoncz, odswiez };
}

export interface StanNowegoTreningu {
  /** Trwa zakladanie treningu. Po sukcesie zostaje true az do przejscia na nowy ekran: drugi klik nie zalozy drugiego. */
  zajety: boolean;
  blad: BladTreningu | null;
  /**
   * Serwer nie zna analizy podanej jako zrodlo (404 nie_ma_takiej_sesji): kolejne proby maja ja pominac, zeby nie
   * konczyly sie tym samym bledem w kolko. Wywolujacy sprawdza to w `przygotuj` (wartosc z chwili klikniecia).
   */
  zrodloNiewazne: boolean;
  /**
   * Zaklada trening i przechodzi na jego ekran. `przygotuj` zwraca dane zalozenia (moze tez rzucic: blad trafi do `blad`,
   * jak kazdy blad zalozenia).
   */
  zaloz: (przygotuj: () => ZalozenieTreninguDto | Promise<ZalozenieTreninguDto>) => Promise<void>;
}

export function useNowyTrening(): StanNowegoTreningu {
  const navigate = useNavigate();
  const zajeteRef = useRef(false);
  const [zajety, setZajety] = useState(false);
  const [blad, setBlad] = useState<BladTreningu | null>(null);
  const [zrodloNiewazne, setZrodloNiewazne] = useState(false);

  const zaloz = useCallback(
    async (przygotuj: () => ZalozenieTreninguDto | Promise<ZalozenieTreninguDto>): Promise<void> => {
      if (zajeteRef.current) return;
      zajeteRef.current = true;
      setZajety(true);
      setBlad(null);
      try {
        const wejscie = await przygotuj();
        const nowy = await treningApi.zaloz(wejscie);
        navigate(`/cwiczenia/${encodeURIComponent(nowy.id)}`);
      } catch (e) {
        const opis = opisBleduTreningu(e);
        if (opis.kod === KOD_NIE_MA_ZRODLA) setZrodloNiewazne(true);
        setBlad(opis);
        zajeteRef.current = false;
        setZajety(false);
      }
    },
    [navigate],
  );

  return { zajety, blad, zrodloNiewazne, zaloz };
}
